import { useQuery } from '@apollo/client';
import { LICENSE_STATUS, PRODUCT_TYPE } from '~../__queries__';
import { LICENSES_BY_COMPANY_ID } from '~Queries/licenses';
import {
  GetCompanyLicenses,
  GetCompanyLicensesVariables,
  GetCompanyLicenses_Company_licenses,
} from '~Queries/__queries__';
import { useSelector } from '~Hooks';

export const useCompanyLicenses = () => {
  const companyId = useSelector((state) => state.company);
  const demoMode = useSelector((state) => state.demoMode);

  // Get active licenses on the company
  const { data, loading } = useQuery<GetCompanyLicenses, GetCompanyLicensesVariables>(
    LICENSES_BY_COMPANY_ID,
    {
      skip: !companyId,
      variables: {
        id: companyId,
      },
    },
  );

  /**
   * Find a license based on either an id or plan
   */
  const findLicenseByIdOrPlan = (
    idOrPlan: string,
    licenses: GetCompanyLicenses_Company_licenses[] = data?.Company?.licenses || [],
  ): GetCompanyLicenses_Company_licenses | undefined =>
    licenses.find((license) => license.id === idOrPlan || license.plan === idOrPlan);

  /**
   * Check if a specific license is active
   */
  const hasActiveLicense = (plan: PRODUCT_TYPE): boolean => {
    if (demoMode) return true;

    const activeLicenses = data?.Company?.licenses.filter(
      (l) => l.status === LICENSE_STATUS.ACTIVE,
    );
    const license = findLicenseByIdOrPlan(plan, activeLicenses);
    return !!license;
  };

  /**
   * Check if any of the provided licenses are active
   */
  const hasAnyActiveLicense = (plans: PRODUCT_TYPE[]): boolean => {
    if (demoMode) return true;

    const activeLicenses = data?.Company?.licenses.filter(
      (l) => l.status === LICENSE_STATUS.ACTIVE,
    );

    const license = activeLicenses?.find((l) => plans.includes(l.plan as PRODUCT_TYPE));
    return !!license;
  };

  return {
    licenses: data?.Company?.licenses,
    loading,
    findLicenseByIdOrPlan,
    hasActiveLicense,
    hasAnyActiveLicense,
  };
};
